import React, { useContext, useState, useEffect, useRef } from "react";
import { View, StyleSheet, ScrollView, ActivityIndicator } from "react-native";
import { SessionContext } from "../../../../contexts";
import OnboardingUI from "./OnboardingUI";
import { Banner } from "../../../common/native";
import { mixpanel } from "../../../../utils/mixpanel";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";
import { ModalOpener } from "../../../uncommon/native/modals";
import { getClinicUsage, TClinicUsageResponse } from "../../../../services/api";
import { initializeClinicDataIfNeeded } from "./initializeClinicData";
import NotificationsSection from "./NotificationsSection";

const HomeLandingPage: React.FC = () => {
  const session = useContext(SessionContext)?.session;
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [clinicUsage, setClinicUsage] = useState<TClinicUsageResponse | null>(
    null,
  );

  const { setOnboardingModal } = useContext(SessionContext)!;
  const {
    setTourActive,
    setShowSimulateTips,
    setShowBeforeAfterTips,
    setShowConsultTips,
    setShowDeployWidgetTips,
  } = useOnboardingSettings();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const closeUpgradePrompt = () => setShowUpgradePrompt(false);
  const hasInitializedData = useRef(false);

  const isAccountOlderThanOneMonth = (() => {
    if (!session?.createdAt) return false;
    const createdAtDate = new Date(session.createdAt);
    if (Number.isNaN(createdAtDate.getTime())) return false;

    const oneMonthAfterSignup = new Date(createdAtDate);
    oneMonthAfterSignup.setMonth(oneMonthAfterSignup.getMonth() + 1);
    return Date.now() > oneMonthAfterSignup.getTime();
  })();

  if (!session) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00B7F4" />
      </View>
    );
  }

  useEffect(() => {
    const fetchClinicUsage = async () => {
      if (!session) return;

      try {
        const usage = await getClinicUsage(session);
        setClinicUsage(usage);
      } catch (err) {
        console.error("Failed to fetch clinic usage", err);
      }
    };

    fetchClinicUsage();
  }, [session]);

  useEffect(() => {
    if (!session?.createdAt && !session?.freeTrialEndDate) return;

    const trialEndDate = session.freeTrialEndDate
      ? new Date(session.freeTrialEndDate)
      : new Date(session.createdAt);
    if (!session.freeTrialEndDate) {
      trialEndDate.setDate(trialEndDate.getDate() + 7);
    }
    if (Number.isNaN(trialEndDate.getTime())) return;

    const now = new Date();
    const diffTime = trialEndDate.getTime() - now.getTime();
    const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

    setDaysLeft(diffDays);
  }, [session?.createdAt, session?.freeTrialEndDate]);

  useEffect(() => {
    if (!hasInitializedData.current && session?.clinicId) {
      hasInitializedData.current = true;
      console.log("🚀 Initializing clinic data (first time only)");
      initializeClinicDataIfNeeded(session);
    }
  }, [session?.clinicId]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      {/* {showUpgradePrompt && <UpgradePromptModal onClose={closeUpgradePrompt} />} */}
      {showUpgradePrompt && (
        <ModalOpener
          visible={true}
          modalType="UpgradePrompt"
          onClose={closeUpgradePrompt}
          usageStats={clinicUsage}
        />
      )}

      {typeof daysLeft === "number" && daysLeft > 0 && (
        <Banner
          type="trial"
          text={`⏳ ${daysLeft} days left to transform your clinic.`}
          daysLeft={daysLeft}
        />
      )}

      <View style={styles.contentContainer}>
        <OnboardingUI
          title="Run Your Clinic on Autopilot."
          description="First time? Take a tour of the app to get started."
          primaryActionText="Take a Tour"
          showTourCard={!isAccountOlderThanOneMonth}
          onPrimaryAction={() => {
            mixpanel.track("Onboarding Started", {
              "User Email": session?.userEmail,
              "Clinic Name": session?.clinicName,
            });
            setOnboardingModal({ visible: true, type: "OnboardingWelcome" });
            setTourActive(true);
            setShowDeployWidgetTips(true);
            setShowSimulateTips(true);
            const isLeadCaptureGoal = session?.primaryGoal === "lead_capture";
            setShowBeforeAfterTips(!isLeadCaptureGoal);
            setShowConsultTips(!isLeadCaptureGoal);
          }}
          notificationsSlot={<NotificationsSection session={session} />}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    alignItems: "center",
  },
  contentContainer: {
    width: "100%",
    maxWidth: 1200,
    alignItems: "center",
  },
  carouselContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 32,
    marginTop: 48,
    width: "100%",
    paddingHorizontal: 16,
  },
  card: {
    width: "90%",
    maxWidth: 400,
    minWidth: 300,
    backgroundColor: "#f9f9f9",
    borderRadius: 16,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
    alignItems: "center",
  },
  cardPressed: {
    transform: [{ scale: 0.98 }],
    shadowOpacity: 0.08,
  },
  cardImage: {
    width: "100%",
    aspectRatio: 1 / 1,
    borderRadius: 12,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 18,
    color: "#111",
    marginBottom: 8,
    textAlign: "center",
  },
  cardDescription: {
    fontSize: 16,
    color: "#444",
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 8,
  },
  cardTagline: {
    fontSize: 16,
    fontStyle: "italic",
    color: "#111",
    textAlign: "center",
  },
  navButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#eee",
  },
  navText: {
    fontSize: 24,
    textAlign: "center",
    color: "#333",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
    minHeight: "100%",
  },
});

export default HomeLandingPage;
